import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { ImportCandidate, AiImportAssetType } from "@/utils/importUtils";
import { Loader2, HelpCircle, ExternalLink } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CreatePortfolioDialog } from "@/components/portfolio/CreatePortfolioDialog";
import type { CheckedState } from "@radix-ui/react-checkbox";

interface AiImportReviewProps {
  items: ImportCandidate[];
  onEdit: (id: string | undefined, patch: Partial<ImportCandidate>) => void;
  onRescan?: () => void;
  onCommit?: () => void;
  importableCount?: number;
  // Portfolio selection
  portfolios?: Array<{ id: string; name: string; portfolio_type?: string }>;
  selectedPortfolioId?: string | null;
  onSelectPortfolio?: (id: string) => void;
  isCommitting?: boolean;
  onCreatePortfolio?: (name: string, type: string) => void;
  // Currency list for per-row dropdown only
  currencies?: Array<{ code: string; name?: string; symbol?: string }>;
}

const ASSET_TYPES: AiImportAssetType[] = [
  "cash",
  "equity",
  "fixed_income",
  "alternative",
  "multi_asset",
  "unclassified",
];

// Minimal symbol map fallback when currencies table lacks symbol
const symbolMap: Record<string, string> = {
  CAD: '$',
  USD: '$',
  EUR: '€',
  GBP: '£',
  AUD: '$',
  NZD: '$',
  JPY: '¥',
  CHF: 'Fr',
};

export function AiImportReview({ items, onEdit, onRescan, onCommit, importableCount, portfolios, selectedPortfolioId, onSelectPortfolio, isCommitting, onCreatePortfolio, currencies }: AiImportReviewProps) {
  const counts = useMemo(() => ({
    total: items?.length || 0,
    duplicates: items?.filter(i => i.duplicate_hint)?.length || 0,
    importable: items?.filter(i => !i.skip)?.length || 0,
  }), [items]);

  // Do not allow committing while any included row is unclassified
  const hasUnclassifiedIncluded = useMemo(() => (
    Array.isArray(items) && items.some(i => !i.skip && String(i.asset_type).toLowerCase() === 'unclassified')
  ), [items]);

  // Header select-all checkbox state (ignores unclassified rows which cannot be selected)
  const selectableRows = useMemo(() => (items || []).filter(i => String(i.asset_type).toLowerCase() !== 'unclassified'), [items]);
  const selectedCount = useMemo(() => selectableRows.filter(i => !i.skip).length, [selectableRows]);
  const totalSelectable = selectableRows.length;
  const headerChecked: CheckedState = totalSelectable === 0
    ? false
    : (selectedCount === 0 ? false : (selectedCount === totalSelectable ? true : 'indeterminate'));

  const handleToggleAll = (state: CheckedState) => {
    const turnOn = state === true || (state as any) === 'indeterminate';
    const target = turnOn ? selectableRows : (items || []);
    for (const r of target) {
      onEdit(r.id, { skip: turnOn ? false : true });
    }
  };

  return (
    <div className="p-6 md:p-7 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">AI Import</div>
          <h2 className="text-xl font-semibold">Review extracted assets</h2>
        </div>
        <div className="flex items-center gap-2">
          {Array.isArray(portfolios) && portfolios.length > 0 && (
            <Select value={selectedPortfolioId || ""} onValueChange={(v) => onSelectPortfolio?.(v)}>
              <SelectTrigger className="w-[220px] h-8 text-xs">
                <SelectValue placeholder="Select portfolio" />
              </SelectTrigger>
              <SelectContent>
                {(portfolios || [])
                  .filter(p => {
                    const t = (p as any)?.portfolio_type;
                    return t === 'personal' || t === 'client';
                  })
                  .map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
          {onCreatePortfolio && (
            <CreatePortfolioDialog
              onCreatePortfolio={(name, type) => onCreatePortfolio?.(name, type)}
              buttonClassName="gap-1"
              defaultType="personal"
              defaultName="Imported Portfolio"
              allowedTypes={["personal","client"]}
            />
          )}
          <Button variant="outline" size="sm" onClick={onRescan} disabled={!!isCommitting}>Rescan</Button>
          {/* Block commit if any included row is unclassified */}
          <Button size="sm" onClick={onCommit} disabled={!!isCommitting || (importableCount ?? counts.importable) === 0 || hasUnclassifiedIncluded}>
            {isCommitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Importing…
              </span>
            ) : (
              <>Import {importableCount ?? counts.importable} rows</>
            )}
          </Button>
        </div>
      </div>

      {hasUnclassifiedIncluded && (
        <div className="text-xs text-destructive">
          One or more included rows are <span className="font-semibold">unclassified</span>. Please choose one of: cash, equity, fixed_income, alternative, or multi_asset.
        </div>
      )}

      <div className="text-xs text-muted-foreground">
        {counts.total} rows • {counts.duplicates} potential duplicates • {counts.importable} selected
      </div>

      {/* Scrollable, compact table */}
      <div className="border rounded-md overflow-hidden text-xs">
        <div className="max-h-[52vh] overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-background">
              <TableRow className="h-9">
                <TableHead className="w-[36px] px-0 py-1.5 text-center">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={headerChecked}
                            onCheckedChange={handleToggleAll}
                            aria-label="Select all"
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="text-xs">Select/Deselect all</div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableHead>
                <TableHead className="py-1.5">Asset</TableHead>
                <TableHead className="py-1.5 w-[110px]">Type</TableHead>
                <TableHead className="py-1.5 w-[88px]">Symbol</TableHead>
                <TableHead className="py-1.5 w-[96px]">
                  <div className="flex items-center gap-1">
                    Currency
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" aria-label="Currency help" className="h-4 w-4 text-muted-foreground hover:text-foreground">
                            <HelpCircle className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="text-xs max-w-[260px]">
                            Options are loaded from the 'currencies' table. Use ISO 4217 codes.
                            <a href="https://en.wikipedia.org/wiki/ISO_4217" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 ml-2">ISO list <ExternalLink className="h-3 w-3" /></a>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </TableHead>
                <TableHead className="text-right py-1.5 w-[100px]">Qty</TableHead>
                <TableHead className="text-right py-1.5 w-[120px]">Market Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(items || []).map((row) => (
                <TableRow key={row.id || `${row.asset_name}-${row.symbol || ''}-${row.source_row || ''}`} className="h-9">
                  <TableCell className="py-1.5 w-[36px] px-0">
                    <div className="flex items-center justify-center">
                      <Checkbox
                        checked={!row.skip}
                        onCheckedChange={(checked) => {
                          onEdit(row.id, { skip: checked ? false : true });
                        }}
                        aria-label="Include in import"
                        title={String(row.asset_type).toLowerCase() === 'unclassified' ? 'Unclassified rows can be unchecked. Classify to include.' : undefined}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="min-w-[220px] py-1.5">
                    <div className="flex items-center gap-1.5">
                      {String(row.asset_type).toLowerCase() === 'unclassified'
                        ? (
                          <Badge variant="destructive" className="text-[9px]" title="Classification required">WARN</Badge>
                        )
                        : (
                          row.duplicate_hint
                            ? <Badge variant="secondary" className="text-[9px]">DUP</Badge>
                            : <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-[9px]">NEW</Badge>
                        )}
                      <Input
                        defaultValue={row.asset_name}
                        onChange={(e) => onEdit(row.id, { asset_name: e.target.value })}
                        className="h-8 text-xs"
                      />
                    </div>
                    {row.duplicate_hint && (
                      <div className="text-[10px] text-muted-foreground mt-1">
                        Potential duplicate: {row.duplicate_hint.reason} (score {Math.round((row.duplicate_hint.score || 0) * 100)}%)
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="py-1.5 w-[110px]">
                    <Select defaultValue={row.asset_type} onValueChange={(v) => onEdit(row.id, { asset_type: v as AiImportAssetType })}>
                      <SelectTrigger className="w-[110px] h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ASSET_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="py-1.5 w-[88px]">
                    <Input
                      defaultValue={row.symbol || ''}
                      onChange={(e) => onEdit(row.id, { symbol: e.target.value })}
                      placeholder="—"
                      className="h-8 text-xs w-[80px]"
                    />
                  </TableCell>
                  <TableCell className="py-1.5 w-[96px]">
                    <Select defaultValue={(row.currency || '').toUpperCase()} onValueChange={(v) => onEdit(row.id, { currency: v })}>
                      <SelectTrigger className="w-[96px] h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Array.isArray(currencies) && currencies.length > 0
                        ? currencies.map((c) => {
                            const code = (c.code || '').toUpperCase();
                            const sym = (c.symbol || symbolMap[code] || '').trim();
                            const label = sym ? `${sym} ${code}` : code;
                            return (
                              <SelectItem key={code} value={code}>{label}</SelectItem>
                            );
                          })
                        : ['CAD','USD','EUR','GBP','AUD'].map(code => (
                            <SelectItem key={code} value={code}>{(symbolMap[code] ? `${symbolMap[code]} ${code}` : code)}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right py-1.5 w-[100px]">
                    <Input
                      defaultValue={
                        typeof row.quantity === 'number'
                          ? row.quantity.toFixed(2)
                          : (row.quantity ?? '')
                      }
                      onChange={(e) => onEdit(row.id, { quantity: e.target.value ? Number(e.target.value) : undefined })}
                      className="text-right h-8 text-xs"
                    />
                  </TableCell>
                  <TableCell className="text-right py-1.5 w-[120px]">
                    <Input
                      defaultValue={
                        typeof row.market_value === 'number'
                          ? row.market_value.toFixed(2)
                          : ''
                      }
                      onChange={(e) => onEdit(row.id, { market_value: Number(e.target.value || 0) })}
                      className="text-right h-8 text-xs"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">You can adjust any field before import. Duplicates will be merged if you choose to import them.</p>
    </div>
  );
}

export default AiImportReview;
